import {
  ReportSeveritySummary,
  ReportStatusSummary
} from "../../../hooks/reports/useReportSummary";
import axios from "../../axios";
import { CTBReportAPIResponse, parseReport, CTBReport } from "./parseReports";
import { ReportsInfo, TargetsInfo } from "./parseTarget";
import { TriageStatus } from "../usersApi";
import ctbOwaspHierarchy from "./ctb_owasp_hierarchy.json";

const URL = "/v2/reports";

export enum ReportStatus {
  SUBMITTED,
  DRAFT,
  DELETED
}

export type ReportQueryFilters = {
  severity?: string;
  status?: string;
  reportState?: string;
  type?: string;
  search?: string;
  adminState?: TriageStatus;
  userId?: number;
  assetId?: number;
  targetId?: number;
  isDelete?: boolean;
  page?: string;
  pageCount?: string;
  limit?: number;
  includeArchived?: boolean | string;
  includeClosed?: boolean;
  program?: string;
  pentesterUsername?: string;
};

export type ReportUpdateFields = Omit<
  CTBReport,
  "id" | "creator" | "program" | "state" | "createdAt" | "updatedAt"
> & {
  attachments?: File[];
  assetId?: string;
  targetIds?: string[];
  endpointIds?: number[];
  runId?: number;
  isDraft?: boolean;
};

export type FilterOptions = {
  programNames: { id: number; name: string }[];
  vulnerabilityTypes: string[];
  severityCategories: string[];
  reportStates: string[];
  submissionDates: string[];
  reportIds: string[];
  pentesters: string[];
};

export const getFilterOptions = async (): Promise<FilterOptions> => {
  const response = await axios.get("/v2/reports/filterOptions");
  const data = response.data.data;

  const filterOptions: FilterOptions = {
    programNames: data.programNames.map(
      (program: { id: number; name: string }) => ({
        id: program.id,
        name: program.name
      })
    ),
    vulnerabilityTypes: data.vulnerabilityTypes,
    severityCategories: data.severityCategories,
    reportStates: data.reportStates,
    submissionDates: data.submissionDates,
    reportIds: data.reportIds.map((id: number) => id.toString()),
    pentesters: data.pentesters
  };

  return filterOptions;
};

export enum ReportState {
  BUSINESS_REVIEW = "Business Review",
  BUSINESS_APPROVED = "Business Approved",
  BUSINESS_REJECTED = "Business Rejected",
  BUSINESS_REQUEST_INFO = "Request more info",
  BUSINESS_ACCEPT_RISK = "Business Accepts Risk",
  BUSINESS_NON_ACTIONABLE_ISSUE = "Business Non Actionable Issue",
  AWAITING_FIX = "Awaiting Fix",
  OUT_OF_SCOPE = "out of scope",
  // QA_DISAGREES = "QA Disagrees",
  QA_MODIFIED = "QA Modified",
  TRIAGE_APPROVED = "Triage Approved",
  TRIAGE_REJECTED = "Triage Rejected",
  UNDER_REVIEW = "underReview",
  RECEIVED = "received",
  CLOSED = "closed",
  PAYMENT_INITIATED = "Payment Initiated",
  TRIAGE_NOT_OPTED_FE_ROUTE = "triage-not-opted",
  TRIAGE_SERVICE_NOT_OPTED = "Service Not Opted",
  DELETED = "deleted",
  RETEST_REQUESTED = "Retest Requested",
  RETEST_IN_PROCESS = "Retest In Process",
  FIX_VERIFIED = "Fix Verified",
  FIX_FAILED = "Fix Failed",
  QA_REVIEW_IN_PROCESS = "QA Review In Process",
  FIX_APPROVED = "Fix Approved",
  FIX_REJECTED = "Fix Rejected",
  FINDINGS_REJECTED = "Findings Rejected",
  NEED_INFORMATION = "Need Information",
  FIX_IN_PROGRESS = "Fix in Progress",
  RISK_ACCEPTED_AND_CLOSED = "Risk Accepted and Closed",
  CLOSE_RETEST = "Close Retest",
  REOPEN_RETEST = "Reopen Retest",
  REQUEST_FURTHER_ACTION = "Request Further Action"
}

/**
 * Helper to determine if a report state should be considered "Resolved" or "Closed".
 * These states do NOT trigger an archive warning about "open findings".
 */
export const isReportClosed = (state: ReportState | string | undefined): boolean => {
  if (!state) return false;

  const closedStates = [
    ReportState.CLOSED,
    ReportState.RISK_ACCEPTED_AND_CLOSED,
    ReportState.CLOSE_RETEST,
    ReportState.FIX_VERIFIED,
    ReportState.OUT_OF_SCOPE,
    ReportState.BUSINESS_REJECTED,
    ReportState.TRIAGE_REJECTED,
    ReportState.FINDINGS_REJECTED,
    ReportState.BUSINESS_ACCEPT_RISK,
    ReportState.BUSINESS_NON_ACTIONABLE_ISSUE,
    ReportState.FIX_APPROVED
  ];

  return closedStates.includes(state as ReportState);
};

export const getReportSummary = async () => {
  const response = (await axios.get(URL + "/summary")).data as {
    latest: CTBReportAPIResponse[];
    status: ReportStatusSummary;
    severity: ReportSeveritySummary;
  };

  return {
    latest: response.latest.map(report => parseReport(report)),
    status: response.status,
    severity: response.severity
  };
};

export const getCertificate = async (reportId: number) => {
  return (
    await axios.get(URL + `/certificate?id=${reportId}`, {
      responseType: "blob"
    })
  ).data as Blob;
};

// Vulnerability types
export type CWE = {
  id: string;
  name: string;
};

export type VulnerabilityCategory = {
  id: string;
  name: string;
  type: string;
  priority?: number | null;
  owasp_id?: string;
  cwe?: CWE[] | string;
  children?: VulnerabilityCategory[];
};

export const VULNERABILITY_CATEGORIES: VulnerabilityCategory[] = ctbOwaspHierarchy as VulnerabilityCategory[];

export const parseReportsInfo = (response: any): ReportsInfo => {
  const data = response.data || {};

  return {
    program: {
      id: data.program?.id ?? -1,
      name: data.program?.name || "Unknown Program",
      total_reports: data.program?.total_reports || 0
    },
    summary: {
      reports_received_90_days: data.summary?.reports_received_90_days || 0,
      reports_resolved: data.summary?.reports_resolved || 0,
      assets_in_scope: data.summary?.assets_in_scope || 0,
      report_status_counts: data.summary?.report_status_counts || {},
      closed_by_retest_report_ids:
        Array.isArray(data.summary?.closed_by_retest_report_ids)
          ? data.summary.closed_by_retest_report_ids
          : []
    },
    reports: Array.isArray(data.reports)
      ? data.reports.map((report: any) => ({
        id: report.id ?? -1,
        title: report.title || "Untitled",
        status: report.status || "Unknown",
        retest_status: report.retest_status ?? null,
        category: report.category || "Uncategorized",
        severity: report.severity || "Low",
        submitted_date: report.submitted_date || "Unknown Date"
      }))
      : []
  };
};

export const parseTargetsInfo = (data: any): TargetsInfo => {
  return {
    program_id: data.program_id,
    total_targets: data.total_targets,
    targets: data.targets.map((target: any) => ({
      name: target.name,
      type: target.type
    }))
  };
};
